import {
  DocumentTextIcon,
  EyeIcon,
  ClockIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  ClipboardDocumentListIcon,
  ArrowDownCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  buildSaveCasePaperPayload,
  searchCasePaperDoses,
  searchCasePaperDrugs,
  searchCasePaperServices,
} from "../../services/casePaperService";
import {
  clearCasePaper,
  loadCasePaperByVisit,
  saveCasePaper,
} from "../../store/slices/casePaperSlice";
import CommonCheckbox from "../common11/formInputs/CommonCheckbox";
import InputField from "../common11/formInputs/InputField";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import CommonModal from "../common11/commonModals/CommonModal";
import CommonButton from "../common11/Buttons/CommonButton";
import CertificateFormModal from "./CertificateFormModal";
import OPDBilling from "../bill/OPDBilling";
import TestCompareModal from "./TestCompareModal";
import ReferPatientModal from "./ReferPatientModal";
import ProceedToAdmissionModal from "./ProceedToAdmissionModal";
import AddFilesDocsModal from "./AddFilesDocsModal";
import {
  buildClinicalFields,
  CasePaperGenericMainSection,
  CasePaperPrescriptionModal,
  CasePaperServicesModal,
} from "./CasePaperGenericBody";
import { GeneratePrint } from "../common/printModal/GeneratePrint";
import { showErrorToast, showInfoToast, showSuccessToast } from "../common11/toastUtils";

// Static options for History & findings search dropdowns (can be replaced with API)
const chiefComplaintOptions = ["Headache", "Fever", "Cough", "Cold", "Pain", "Weakness", "Nausea", "Dizziness", "Chest pain", "Breathlessness"].map((l, i) => ({ id: i, label: l, value: l }));
const historyOptions = ["Diabetes", "HTN", "Asthma", "CAD", "Thyroid", "TB", "No significant history"].map((l, i) => ({ id: i, label: l, value: l }));
const allergyOptions = ["No known allergy", "Drug allergy", "Penicillin", "Sulfa", "Food allergy", "Dust"].map((l, i) => ({ id: i, label: l, value: l }));
const examinationOptions = ["General", "Systemic", "CNS", "CVS", "RS", "Abdomen", "Skin", "ENT"].map((l, i) => ({ id: i, label: l, value: l }));
const findingsOptions = ["Normal", "Within normal limits", "NAD", "To be examined"].map((l, i) => ({ id: i, label: l, value: l }));
const diagnosisOptions = ["Diabetes Mellitus Type 2", "Vertigo", "HTN", "Asthma", "COPD", "CAD", "ARI", "UTI", "Other"].map((l, i) => ({ id: i, label: l, value: l }));
const followUpInstructionOptions = [
  "Review after 3 days",
  "Review after 1 week",
  "Review after 2 weeks",
  "Review after 1 month",
  "If symptoms worsen, come earlier",
  "Bring previous reports",
].map((l, i) => ({ id: i, label: l, value: l }));

// Disease-wise EMR templates – select one to patch entire form
const emrTemplates = [
  {
    id: "dengue",
    label: "Dengue",
    value: "dengue",
    data: {
      chiefComplaints: "High grade fever\nBody ache\nHeadache\nRash",
      history: "No significant history",
      allergy: "No known allergy",
      examinations: "General\nSkin",
      findings: "Petechial rash, positive tourniquet test",
      diagnosis: "Dengue Fever",
      bp: "90/60",
      pulse: "100",
      spo2: "98",
      weight: "",
      height: "",
      rbs: "",
      temp: "102",
      resp: "20",
      bmi: "",
      myThought: "Dengue suspected. Monitor platelets. Advise CBC, NS1 antigen.",
      followUpInstructions: "Review after 3 days\nIf platelets drop, refer immediately",
      prescriptions: [
        { drugName: "PARACETAMOL 500MG TAB", genericName: "PARACETAMOL", dose: "1-1-1", duration: "5", durIn: "Days", totalQty: "15", instruction: "SOS for fever", remark: "" },
        { drugName: "ORS", genericName: "ORS", dose: "As directed", duration: "5", durIn: "Days", totalQty: "5", instruction: "Adequate hydration", remark: "" },
      ],
      investServices: [
        { code: "CBC", serviceName: "Complete Blood Count", qty: "1" },
        { code: "NS1", serviceName: "Dengue NS1 Antigen", qty: "1" },
      ],
    },
  },
  {
    id: "ari",
    label: "Acute Respiratory Infection (ARI)",
    value: "ari",
    data: {
      chiefComplaints: "Cough\nCold\nFever",
      history: "No significant history",
      allergy: "No known allergy",
      examinations: "General\nRS",
      findings: "NAD",
      diagnosis: "ARI",
      bp: "",
      pulse: "",
      spo2: "",
      weight: "",
      height: "",
      rbs: "",
      temp: "",
      resp: "",
      bmi: "",
      myThought: "Upper respiratory tract infection. Symptomatic treatment.",
      followUpInstructions: "Review after 3 days",
      prescriptions: [
        { drugName: "PARACETAMOL 500MG TAB", genericName: "PARACETAMOL", dose: "1-1-0", duration: "3", durIn: "Days", totalQty: "9", instruction: "For fever", remark: "" },
        { drugName: "LEVOCETIRIZINE 5MG TAB", genericName: "LEVOCETIRIZINE", dose: "0-0-1", duration: "5", durIn: "Days", totalQty: "5", instruction: "For cold", remark: "" },
      ],
      investServices: [],
    },
  },
  {
    id: "dm2",
    label: "Diabetes Mellitus Type 2",
    value: "dm2",
    data: {
      chiefComplaints: "Increased thirst\nIncreased urination\nWeakness",
      history: "Diabetes",
      allergy: "No known allergy",
      examinations: "General",
      findings: "NAD",
      diagnosis: "Diabetes Mellitus Type 2",
      bp: "130/80",
      pulse: "80",
      spo2: "98",
      weight: "",
      height: "",
      rbs: "180",
      temp: "",
      resp: "",
      bmi: "",
      myThought: "Continue DM medications. Diet and exercise advice. Monitor RBS.",
      followUpInstructions: "Review after 1 week\nBring fasting blood sugar report",
      prescriptions: [
        { drugName: "METFORMIN 500MG TAB", genericName: "METFORMIN", dose: "1-1-1", duration: "30", durIn: "Days", totalQty: "90", instruction: "After food", remark: "" },
      ],
      investServices: [
        { code: "RBS", serviceName: "Random Blood Sugar", qty: "1" },
        { code: "HbA1c", serviceName: "HbA1c", qty: "1" },
      ],
    },
  },
  {
    id: "hypertension",
    label: "Hypertension",
    value: "hypertension",
    data: {
      chiefComplaints: "Headache\nDizziness",
      history: "HTN",
      allergy: "No known allergy",
      examinations: "General\nCVS",
      findings: "BP elevated",
      diagnosis: "HTN",
      bp: "150/95",
      pulse: "82",
      spo2: "98",
      weight: "",
      height: "",
      rbs: "",
      temp: "",
      resp: "",
      bmi: "",
      myThought: "Hypertension. Start/continue antihypertensive.",
      followUpInstructions: "Review after 2 weeks\nMonitor BP at home",
      prescriptions: [
        { drugName: "ATORVASTATIN 20MG TAB", genericName: "ATORVASTATIN", dose: "0-0-1", duration: "30", durIn: "Days", totalQty: "30", instruction: "At bedtime", remark: "" },
      ],
      investServices: [
        { code: "ECG", serviceName: "ECG", qty: "1" },
      ],
    },
  },
];

const emrTemplateOptions = emrTemplates.map((t) => ({ id: t.id, label: t.label, value: t.value }));

const durInOptions = ["Days", "Weeks", "Months", "Doses"].map((l, i) => ({ id: i, label: l, value: l }));

// Mock previous prescriptions (replace with API)
const mockPreviousPrescriptions = [
  {
    id: 1,
    date: "05-Mar-2025",
    doctorName: "Dr. Sharma",
    visitType: "OPD",
    medicines: [
      { drugName: "APIDRA CARTRIGE", genericName: "INSULIN CARTRAGE", dose: "1-0-0", duration: "30", durIn: "Days", totalQty: "30", instruction: "Before breakfast", remark: "" },
      { drugName: "ATORFIT CV 20/75MG CAP", genericName: "ATORVASTATIN+CLOPIDOGREL", dose: "1-0-0", duration: "30", durIn: "Days", totalQty: "30", instruction: "After dinner", remark: "" },
      { drugName: "AZITHROMYCIN 500MG TAB", genericName: "AZITHROMYCIN", dose: "1-0-0", duration: "3", durIn: "Days", totalQty: "3", instruction: "Once daily", remark: "" },
    ],
  },
  {
    id: 2,
    date: "28-Feb-2025",
    doctorName: "Dr. Sharma",
    visitType: "OPD",
    medicines: [
      { drugName: "PARACETAMOL 500MG TAB", genericName: "PARACETAMOL", dose: "1-0-1", duration: "3", durIn: "Days", totalQty: "6", instruction: "After meals", remark: "For fever" },
      { drugName: "CEFANTRAL 1GM INJ", genericName: "CEFOTAXIME", dose: "1-1-1", duration: "5", durIn: "Days", totalQty: "15", instruction: "IM", remark: "" },
    ],
  },
  {
    id: 3,
    date: "15-Feb-2025",
    doctorName: "Dr. Gupta",
    visitType: "OPD",
    medicines: [
      { drugName: "METFORMIN 500MG TAB", genericName: "METFORMIN", dose: "1-1-0", duration: "30", durIn: "Days", totalQty: "60", instruction: "With meals", remark: "" },
      { drugName: "OMEPRAZOLE 20MG CAP", genericName: "OMEPRAZOLE", dose: "1-0-0", duration: "14", durIn: "Days", totalQty: "14", instruction: "Empty stomach", remark: "" },
    ],
  },
];

function CasePaper() {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const casePaperId = useSelector((s) => s.casePaper.casePaperId);
  const formDefaults = useSelector((s) => s.casePaper.formDefaults);
  const saving = useSelector((s) => s.casePaper.saving);
  const state = location.state || {};
  const { patientId, patientName, patient } = state;
  const visitId = state.visitId ?? patient?.visitId;

  const methods = useForm({
    defaultValues: {
      uhid: patient?.uhid ?? "",
      opdNo: patient?.opdNo ?? patient?.visitNo ?? "",
      ageGen: patient?.age ? `${patient.age} / ${patient.gender || ""}` : "",
      patName: patientName ?? patient?.patientName ?? "",
      bloodGroup: "",
      docName: patient?.doctorName || patient?.doctor || "",
      refByDoc: null,
      cadIhd: false,
      cadIhdNote: "",
      thyroid: false,
      thyroidNote: "",
      diabetes: false,
      diabetesNote: "",
      htn: false,
      htnNote: "",
      asthma: false,
      tb: false,
      bp: "",
      pulse: "",
      spo2: "",
      weight: "",
      height: "",
      rbs: "",
      temp: "",
      resp: "",
      bmi: "",
      emrTemplateSearch: null,
      chiefComplaints: "",
      chiefComplaintsSearch: null,
      history: "",
      historySearch: null,
      allergy: "",
      allergySearch: null,
      examinations: "",
      examinationsSearch: null,
      findings: "",
      findingsSearch: null,
      diagnosis: "",
      diagnosisSearch: null,
      myThought: "",
      followUp: false,
      followUpDate: "",
      followUpInstructions: "",
      followUpInstructionMulti: [],
      prescriptionMode: "prescNew",
      investTemplate: false,
      prevInvest: "",
      investSuggested: "",
      // Drug selection (search + add to grid)
      drugSearch: null,
      drugGenericName: "",
      drugDose: null,
      drugDuration: "",
      drugDurIn: null,
      drugTotal: "",
      drugInstruction: "",
      drugRemark: "",
      prescriptions: [],
      // Investigation services selection (like services grid)
      investServiceSearch: null,
      investQty: "",
      investServices: [],
    },
  });

  const { control, watch, setValue, setFocus, getValues, reset } = methods;
  const { fields: prescriptionFields, append: appendPrescriptionRow, remove: removePrescriptionRow, replace: replacePrescriptions } = useFieldArray({
    control,
    name: "prescriptions",
  });
  const { fields: investServiceFields, append: appendInvestService, remove: removeInvestService, replace: replaceInvestServices } = useFieldArray({
    control,
    name: "investServices",
  });

  useEffect(() => {
    if (!visitId) return undefined;
    dispatch(loadCasePaperByVisit(visitId));
    return () => {
      dispatch(clearCasePaper());
    };
  }, [dispatch, visitId]);

  useEffect(() => {
    if (!formDefaults) return;
    const current = getValues();
    reset({
      ...current,
      ...formDefaults,
      uhid: current.uhid || patient?.uhid || "",
      opdNo: current.opdNo || patient?.opdNo || patient?.visitNo || "",
      patName: current.patName || patientName || patient?.patientName || "",
      docName: current.docName || patient?.doctorName || patient?.doctor || "",
      ageGen:
        current.ageGen ||
        (patient?.age ? `${patient.age} / ${patient.gender || ""}` : ""),
    });
    replacePrescriptions(formDefaults.prescriptions || []);
    replaceInvestServices(formDefaults.investServices || []);
  }, [formDefaults, getValues, patient, patientName, replaceInvestServices, replacePrescriptions, reset]);

  const handleSaveCasePaper = async () => {
    if (!visitId) {
      showErrorToast("Open case paper from Medical Records queue (visit required)");
      return;
    }
    const form = getValues();
    const payload = buildSaveCasePaperPayload(form, {
      visitId,
      doctorId: patient?.doctorId || 0,
      userId: user?.userId || 0,
    });
    try {
      const result = await dispatch(
        saveCasePaper({ casePaperId, payload })
      ).unwrap();
      showSuccessToast("Case paper saved successfully");
      if (result?.casePaperId) {
        dispatch(loadCasePaperByVisit(visitId));
      }
    } catch (error) {
      showErrorToast(error || "Failed to save case paper");
    }
  };

  const [showPrevFollowUpsModal, setShowPrevFollowUpsModal] = useState(false);
  const [selectedCasePaperForView, setSelectedCasePaperForView] = useState(null);
  const [showAttachedButtons, setShowAttachedButtons] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showOpdBillModal, setShowOpdBillModal] = useState(false);
  const [showTestCompareModal, setShowTestCompareModal] = useState(false);
  const [showReferPatientModal, setShowReferPatientModal] = useState(false);
  const [showProceedAdmModal, setShowProceedAdmModal] = useState(false);
  const [showAddFilesModal, setShowAddFilesModal] = useState(false);
  const [showConditionsPopover, setShowConditionsPopover] = useState(false);
  const [showPrescriptionHistory, setShowPrescriptionHistory] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedPrescriptionForView, setSelectedPrescriptionForView] = useState(null);
  const [drugOptions, setDrugOptions] = useState([]);
  const [doseOptions, setDoseOptions] = useState([]);
  const [investigationServiceOptions, setInvestigationServiceOptions] = useState([]);
  const [drugsLoading, setDrugsLoading] = useState(false);
  const [dosesLoading, setDosesLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [isDuplicateDrug, setIsDuplicateDrug] = useState(false);
  const attachedMenuRef = useRef(null);
  const conditionsMenuRef = useRef(null);
  const conditionsModalRef = useRef(null);
  const [conditionsModalPosition, setConditionsModalPosition] = useState({ top: 0, right: 0 });

  // Refs for prescription field focusing (Drug Name -> Generic -> Dose -> Duration -> Dur.In -> Total ...)
  const drugDoseWrapperRef = useRef(null);
  const drugDurationWrapperRef = useRef(null);
  const drugSearchWrapperRef = useRef(null);
  const drugDurInWrapperRef = useRef(null);
  const drugTotalWrapperRef = useRef(null);
  const drugAddButtonRef = useRef(null);
  const lastIpdDsPrintRef = useRef(null);
  const prescriptionTableScrollRef = useRef(null);
  const investigationTableScrollRef = useRef(null);

  /** Scroll after field-array updates (double rAF so layout includes new rows) */
  const scrollPrescriptionTableToBottom = () => {
    const el = prescriptionTableScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };

  const scrollInvestigationTableToBottom = () => {
    const el = investigationTableScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };

  const handleLastIpdDsPrint = () => {
    if (lastIpdDsPrintRef.current) {
      GeneratePrint(lastIpdDsPrintRef);
    }
  };

  const handleOpdBillClick = () => {
    setShowOpdBillModal(true);
  };

  const handleServiceSearch = useCallback(async (term) => {
    setServicesLoading(true);
    try {
      const options = await searchCasePaperServices(term || "");
      setInvestigationServiceOptions(options);
    } catch (error) {
      setInvestigationServiceOptions([]);
      showErrorToast(error.message || "Failed to search services");
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const handleDrugSearch = useCallback(async (term) => {
    setDrugsLoading(true);
    try {
      const options = await searchCasePaperDrugs(term || "");
      setDrugOptions(options);
    } catch (error) {
      setDrugOptions([]);
      showErrorToast(error.message || "Failed to search drugs");
    } finally {
      setDrugsLoading(false);
    }
  }, []);

  const handleDoseSearch = useCallback(async (term) => {
    setDosesLoading(true);
    try {
      const options = await searchCasePaperDoses(term || "");
      setDoseOptions(options);
    } catch (error) {
      setDoseOptions([]);
      showErrorToast(error.message || "Failed to search doses");
    } finally {
      setDosesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showServicesModal) {
      handleServiceSearch("");
    }
  }, [showServicesModal, handleServiceSearch]);

  useEffect(() => {
    if (showPrescriptionModal) {
      handleDrugSearch("");
      handleDoseSearch("");
    }
  }, [showPrescriptionModal, handleDrugSearch, handleDoseSearch]);

  const handleAddInvestService = () => {
    const selected = watch("investServiceSearch");
    const isTemplateMode = !!watch("investTemplate");
    if (!selected) return;

    if (isTemplateMode && selected.data?.services) {
      const services = selected.data.services;
      services.forEach((s) => {
        appendInvestService({
          serviceId: s.serviceId || 0,
          code: s.code ?? "",
          serviceName: s.serviceName ?? "",
          qty: s.qty ?? "1",
        });
      });
    } else {
      const serviceId = selected.data?.serviceId ?? selected.value ?? selected.id ?? 0;
      const code = selected.data?.code ?? "";
      const serviceName = selected.label ?? selected.data?.serviceName ?? "";
      if (!serviceName) return;
      appendInvestService({
        serviceId,
        code,
        serviceName,
        qty: "1",
      });
    }

    setValue("investServiceSearch", null);
    scrollInvestigationTableToBottom();
  };

  const handleInvestTemplateToggle = () => {
    const next = !watch("investTemplate");
    setValue("investTemplate", next, { shouldDirty: true });
    setValue("investServiceSearch", null);
  };

  const handleDrugSelect = (selectedOption) => {
    // If drug name is cleared, reset all related fields and remove highlight
    if (!selectedOption) {
      setValue("drugGenericName", "", { shouldDirty: true });
      setValue("drugDose", null, { shouldDirty: true });
      setValue("drugDuration", "", { shouldDirty: true });
      setValue("drugDurIn", null, { shouldDirty: true });
      setValue("drugTotal", "", { shouldDirty: true });
      setValue("drugInstruction", "", { shouldDirty: true });
      setValue("drugRemark", "", { shouldDirty: true });
      setIsDuplicateDrug(false);
      return;
    }

    const drugName = (selectedOption.label ?? selectedOption.value ?? "").trim();
    const normalizedNewName = drugName.toLowerCase();
    const isAlreadyInTable = prescriptionFields.some(
      (row) => (row.drugName ?? "").trim().toLowerCase() === normalizedNewName
    );

    if (isAlreadyInTable) {
      setIsDuplicateDrug(true);
      showInfoToast("Drug is already added in table");
      // Don't move focus further when duplicate
      return;
    }

    // Not duplicate: normal styling, fill generic when API provides it, move focus
    setIsDuplicateDrug(false);
    const genericFromApi =
      selectedOption.genericName ||
      selectedOption.data?.extra ||
      selectedOption.data?.genericName ||
      "";
    if (genericFromApi) {
      setValue("drugGenericName", genericFromApi, { shouldDirty: true });
    }
    setTimeout(() => setFocus("drugGenericName"), 0);
  };

  const focusDrugSearch = () => {
    setTimeout(() => {
      const wrapper = drugSearchWrapperRef.current;
      if (!wrapper) return;
      const focusable = wrapper.querySelector("input, [tabindex='0']");
      if (focusable) focusable.focus();
    }, 0);
  };

  const focusDrugDose = () => {
    setTimeout(() => {
      const wrapper = drugDoseWrapperRef.current;
      if (!wrapper) return;
      const focusable = wrapper.querySelector("input, [tabindex='0']");
      if (focusable) focusable.focus();
    }, 0);
  };

  const focusDrugDuration = () => {
    setTimeout(() => {
      const wrapper = drugDurationWrapperRef.current;
      if (!wrapper) return;
      const input = wrapper.querySelector("input");
      if (input) input.focus();
    }, 0);
  };

  const focusDrugDurIn = () => {
    setTimeout(() => {
      const wrapper = drugDurInWrapperRef.current;
      if (!wrapper) return;
      const focusable = wrapper.querySelector("input, [tabindex='0']");
      if (focusable) focusable.focus();
    }, 0);
  };

  const focusDrugTotal = () => {
    setTimeout(() => {
      const wrapper = drugTotalWrapperRef.current;
      if (!wrapper) return;
      const input = wrapper.querySelector("input");
      if (input) input.focus();
    }, 0);
  };

  const focusDrugAddButton = () => {
    setTimeout(() => {
      if (drugAddButtonRef.current) {
        drugAddButtonRef.current.focus();
      }
    }, 0);
  };

  const handlePatchPrescription = (prescription) => {
    if (!prescription?.medicines?.length) return;
    const existingDrugs = (watch("prescriptions") ?? []).map((r) => (r.drugName ?? "").toLowerCase());
    let added = 0;
    prescription.medicines.forEach((med) => {
      const drugName = (med.drugName ?? "").trim();
      if (!drugName) return;
      if (existingDrugs.includes(drugName.toLowerCase())) return;
      appendPrescriptionRow({
        drugName,
        genericName: med.genericName ?? "",
        dose: med.dose ?? "",
        duration: med.duration ?? "",
        durIn: med.durIn ?? "",
        totalQty: med.totalQty ?? "",
        instruction: med.instruction ?? "",
        remark: med.remark ?? "",
      });
      existingDrugs.push(drugName.toLowerCase());
      added++;
    });
    setShowPrescriptionHistory(false);
    if (added > 0) {
      showInfoToast(`${added} medicine(s) added from prescription`);
      scrollPrescriptionTableToBottom();
    }
  };

  const handlePatchSingleMedicine = (med) => {
    const drugName = (med?.drugName ?? "").trim();
    if (!drugName) return;
    const existingDrugs = (watch("prescriptions") ?? []).map((r) => (r.drugName ?? "").toLowerCase());
    if (existingDrugs.includes(drugName.toLowerCase())) {
      showInfoToast("Medicine already added in prescription");
      return;
    }
    appendPrescriptionRow({
      drugName,
      genericName: med.genericName ?? "",
      dose: med.dose ?? "",
      duration: med.duration ?? "",
      durIn: med.durIn ?? "",
      totalQty: med.totalQty ?? "",
      instruction: med.instruction ?? "",
      remark: med.remark ?? "",
    });
    showInfoToast("Medicine added to prescription");
    scrollPrescriptionTableToBottom();
  };

  const handleTemplateSelect = (selected) => {
    if (!selected?.value) {
      methods.reset();
      showInfoToast("Template cleared. Form reset.");
      return;
    }
    const template = emrTemplates.find((t) => t.value === selected.value);
    if (!template?.data) return;
    const d = template.data;
    setValue("bp", d.bp ?? "");
    setValue("pulse", d.pulse ?? "");
    setValue("spo2", d.spo2 ?? "");
    setValue("weight", d.weight ?? "");
    setValue("height", d.height ?? "");
    setValue("rbs", d.rbs ?? "");
    setValue("temp", d.temp ?? "");
    setValue("resp", d.resp ?? "");
    setValue("bmi", d.bmi ?? "");
    setValue("chiefComplaints", d.chiefComplaints ?? "");
    setValue("history", d.history ?? "");
    setValue("allergy", d.allergy ?? "");
    setValue("examinations", d.examinations ?? "");
    setValue("findings", d.findings ?? "");
    setValue("diagnosis", d.diagnosis ?? "");
    setValue("myThought", d.myThought ?? "");
    setValue("followUpInstructions", d.followUpInstructions ?? "");
    if (Array.isArray(d.prescriptions) && d.prescriptions.length > 0) {
      replacePrescriptions(d.prescriptions.map((p) => ({
        drugName: p.drugName ?? "",
        genericName: p.genericName ?? "",
        dose: p.dose ?? "",
        duration: p.duration ?? "",
        durIn: p.durIn ?? "",
        totalQty: p.totalQty ?? "",
        instruction: p.instruction ?? "",
        remark: p.remark ?? "",
      })));
    } else {
      replacePrescriptions([]);
    }
    if (Array.isArray(d.investServices) && d.investServices.length > 0) {
      replaceInvestServices(d.investServices.map((s) => ({
        code: s.code ?? "",
        serviceName: s.serviceName ?? "",
        qty: s.qty ?? "1",
      })));
    } else {
      replaceInvestServices([]);
    }
    showInfoToast(`Template "${template.label}" applied`);
    if (Array.isArray(d.prescriptions) && d.prescriptions.length > 0) {
      scrollPrescriptionTableToBottom();
    }
    if (Array.isArray(d.investServices) && d.investServices.length > 0) {
      scrollInvestigationTableToBottom();
    }
  };

  const handlePatchCasePaper = (row) => {
    const cp = row?.casePaper;
    if (!cp) {
      showInfoToast("No case paper data to patch");
      return;
    }
    setValue("bp", cp.bp ?? "");
    setValue("pulse", cp.pulse ?? "");
    setValue("spo2", cp.spo2 ?? "");
    setValue("weight", cp.weight ?? "");
    setValue("height", cp.height ?? "");
    setValue("rbs", cp.rbs ?? "");
    setValue("temp", cp.temp ?? "");
    setValue("resp", cp.resp ?? "");
    setValue("bmi", cp.bmi ?? "");
    setValue("chiefComplaints", cp.chiefComplaints ?? "");
    setValue("history", cp.history ?? "");
    setValue("allergy", cp.allergy ?? "");
    setValue("examinations", cp.examinations ?? "");
    setValue("findings", cp.findings ?? "");
    setValue("diagnosis", cp.diagnosis ?? "");
    setValue("myThought", cp.myThought ?? "");
    if (Array.isArray(cp.prescriptions) && cp.prescriptions.length > 0) {
      replacePrescriptions(cp.prescriptions.map((p) => ({
        drugName: p.drugName ?? "",
        genericName: p.genericName ?? "",
        dose: p.dose ?? "",
        duration: p.duration ?? "",
        durIn: p.durIn ?? "",
        totalQty: p.totalQty ?? "",
        instruction: p.instruction ?? "",
        remark: p.remark ?? "",
      })));
    } else {
      replacePrescriptions([]);
    }
    if (Array.isArray(cp.investServices) && cp.investServices.length > 0) {
      replaceInvestServices(cp.investServices.map((s) => ({
        code: s.code ?? "",
        serviceName: s.serviceName ?? "",
        qty: s.qty ?? "1",
      })));
    } else {
      replaceInvestServices([]);
    }
    setShowPrevFollowUpsModal(false);
    showInfoToast("Case paper patched successfully");
    if (Array.isArray(cp.prescriptions) && cp.prescriptions.length > 0) {
      scrollPrescriptionTableToBottom();
    }
    if (Array.isArray(cp.investServices) && cp.investServices.length > 0) {
      scrollInvestigationTableToBottom();
    }
  };

  const handleViewCasePaper = (row) => {
    setSelectedCasePaperForView(row);
  };

  const handleAddDrug = () => {
    const selectedDrug = watch("drugSearch");
    const drugName = selectedDrug?.label ?? "";
    if (!drugName.trim()) return;

    const normalizedNewName = drugName.trim().toLowerCase();
    const isDuplicate = prescriptionFields.some(
      (row) => (row.drugName ?? "").trim().toLowerCase() === normalizedNewName
    );
    if (isDuplicate) {
      return;
    }

    const genericName = String(watch("drugGenericName") ?? "").trim();
    const doseVal = watch("drugDose");
    const dose = typeof doseVal === "object" && doseVal?.label != null ? doseVal.label : String(doseVal ?? "").trim();
    const duration = String(watch("drugDuration") ?? "").trim();
    const durInVal = watch("drugDurIn");
    const durIn = typeof durInVal === "object" && durInVal?.label != null ? durInVal.label : String(durInVal ?? "").trim();
    const total = String(watch("drugTotal") ?? "").trim();
    const instruction = String(watch("drugInstruction") ?? "").trim();
    const remark = String(watch("drugRemark") ?? "").trim();

    const doseId =
      typeof doseVal === "object" && doseVal != null
        ? doseVal.id || doseVal.value || 0
        : 0;

    appendPrescriptionRow({
      drugId: selectedDrug?.id || selectedDrug?.value || 0,
      genericId: 0,
      drugName: drugName.trim(),
      genericName,
      doseId,
      dose,
      duration,
      durIn,
      total,
      totalQty: total,
      instruction,
      remark,
    });

    setValue("drugSearch", null);
    setValue("drugGenericName", "");
    setValue("drugDose", null);
    setValue("drugDuration", "");
    setValue("drugDurIn", null);
    setValue("drugTotal", "");
    setValue("drugInstruction", "");
    setValue("drugRemark", "");
    setTimeout(() => setFocus("drugSearch"), 0);
    scrollPrescriptionTableToBottom();
  };


  // Attached action buttons (different color each) – open horizontally with animation
  const attachedButtons = [
    { id: "lastIpd", label: "Last IPD DS", className: "bg-teal-500 hover:bg-teal-600 text-white border-teal-600" },
    { id: "opdBill", label: "OPD Bill", className: "bg-blue-500 hover:bg-blue-600 text-white border-blue-600" },
    { id: "testCompare", label: "Test Compare", className: "bg-indigo-500 hover:bg-indigo-600 text-white border-indigo-600" },
    { id: "refer", label: "Refer Patient", className: "bg-violet-500 hover:bg-violet-600 text-white border-violet-600" },
    { id: "proceedAdm", label: "Proceed To Adm.", className: "bg-purple-500 hover:bg-purple-600 text-white border-purple-600" },
    { id: "obstHistory", label: "Obst. History", className: "bg-pink-500 hover:bg-pink-600 text-white border-pink-600" },
    { id: "addFiles", label: "Add Files/Docs", className: "bg-rose-500 hover:bg-rose-600 text-white border-rose-600" },
    { id: "certificates", label: "Certificates", className: "bg-amber-500 hover:bg-amber-600 text-white border-amber-600" },
    { id: "reprint", label: "Reprint", className: "bg-cyan-500 hover:bg-cyan-600 text-white border-cyan-600" },
  ];

  // Close attached menu when clicking outside (must be before any early return)
  useEffect(() => {
    if (!showAttachedButtons) return;
    const handleClickOutside = (e) => {
      if (attachedMenuRef.current && !attachedMenuRef.current.contains(e.target)) {
        setShowAttachedButtons(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAttachedButtons]);

  // Compute conditions modal position when it opens (for portal)
  useEffect(() => {
    if (!showConditionsPopover || !conditionsMenuRef.current) return;
    const rect = conditionsMenuRef.current.getBoundingClientRect();
    setConditionsModalPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, [showConditionsPopover]);

  // Close conditions popup when clicking outside (overlay or outside modal)
  useEffect(() => {
    if (!showConditionsPopover) return;
    const handleClickOutside = (e) => {
      const isButton = conditionsMenuRef.current?.contains(e.target);
      const isModal = conditionsModalRef.current?.contains(e.target);
      if (!isButton && !isModal) setShowConditionsPopover(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showConditionsPopover]);

  // Mock previous follow-ups with case paper data (replace with API)
  const prevFollowUps = [
    {
      date: "01/02/2026",
      doctorName: "Dr Tukaram Aute",
      followUpDate: "27/01/2026 03:23 PM",
      casePaper: {
        bp: "120/80",
        pulse: "72",
        spo2: "98",
        weight: "45",
        height: "155",
        rbs: "95",
        temp: "98.6",
        resp: "18",
        bmi: "18.7",
        chiefComplaints: "Headache\nFever",
        history: "Diabetes\nHTN",
        allergy: "No known allergy",
        examinations: "General\nCNS",
        findings: "Normal",
        diagnosis: "Diabetes Mellitus Type 2",
        myThought: "Patient improving.",
        prescriptions: [
          { drugName: "METFORMIN 500MG TAB", genericName: "METFORMIN", dose: "1-1-0", duration: "30", durIn: "Days", totalQty: "60", instruction: "With meals", remark: "" },
          { drugName: "OMEPRAZOLE 20MG CAP", genericName: "OMEPRAZOLE", dose: "1-0-0", duration: "14", durIn: "Days", totalQty: "14", instruction: "Empty stomach", remark: "" },
        ],
        investServices: [{ code: "32089", serviceName: "CBC-Haematology Profile", qty: "1" }],
      },
    },
    {
      date: "17/01/2026",
      doctorName: "Dr Tukaram Aute",
      followUpDate: "27/01/2026 03:23 PM",
      casePaper: {
        bp: "118/78",
        pulse: "75",
        spo2: "97",
        weight: "44",
        height: "155",
        rbs: "102",
        temp: "98.4",
        resp: "16",
        bmi: "18.3",
        chiefComplaints: "Cough",
        history: "Diabetes",
        allergy: "No known allergy",
        examinations: "General",
        findings: "Within normal limits",
        diagnosis: "ARI",
        myThought: "Review after 1 week.",
        prescriptions: [
          { drugName: "PARACETAMOL 500MG TAB", genericName: "PARACETAMOL", dose: "1-0-1", duration: "3", durIn: "Days", totalQty: "6", instruction: "After meals", remark: "" },
          { drugName: "AMOXICILLIN 500MG CAP", genericName: "AMOXICILLIN", dose: "1-1-1", duration: "5", durIn: "Days", totalQty: "15", instruction: "After food", remark: "" },
        ],
        investServices: [],
      },
    },
    {
      date: "04/01/2026",
      doctorName: "Dr Tukaram Aute",
      followUpDate: "22/11/2025 11:50 AM",
      casePaper: {
        bp: "122/82",
        pulse: "78",
        spo2: "99",
        weight: "46",
        height: "155",
        rbs: "88",
        temp: "98.2",
        resp: "18",
        bmi: "19.1",
        chiefComplaints: "Pain",
        history: "HTN",
        allergy: "Drug allergy",
        examinations: "Systemic",
        findings: "NAD",
        diagnosis: "Vertigo",
        myThought: "Stable.",
        prescriptions: [
          { drugName: "ATORVASTATIN 20MG TAB", genericName: "ATORVASTATIN", dose: "1-0-0", duration: "30", durIn: "Days", totalQty: "30", instruction: "After dinner", remark: "" },
        ],
        investServices: [{ code: "31813", serviceName: "X-RAY WRIST", qty: "1" }],
      },
    },
  ];

  const investigationTemplates = [
    { id: "routine-blood", label: "Routine Blood Work", value: "routine-blood", services: [
      { code: "32089", serviceName: "CBC-Haematology Profile" },
      { code: "RBS", serviceName: "RBS" },
      { code: "LFT", serviceName: "Liver Function Test" },
    ]},
    { id: "liver-panel", label: "Liver Function Panel", value: "liver-panel", services: [
      { code: "LFT", serviceName: "Liver Function Test" },
      { code: "SGOT", serviceName: "SGOT" },
      { code: "SGPT", serviceName: "SGPT" },
    ]},
    { id: "thyroid", label: "Thyroid Profile", value: "thyroid", services: [
      { code: "T3", serviceName: "T3" },
      { code: "T4", serviceName: "T4" },
      { code: "TSH", serviceName: "TSH" },
    ]},
  ];

  const investigationTemplateOptions = investigationTemplates.map((t) => ({
    id: t.id,
    label: t.label,
    value: t.value,
    data: t,
  }));

  const clinicalFields = buildClinicalFields({
    chiefComplaintOptions,
    historyOptions,
    allergyOptions,
    examinationOptions,
    findingsOptions,
    diagnosisOptions,
  });

  if (!patientName && !patientId) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex flex-1 items-center justify-center p-6">
          <p className="text-gray-500">No patient selected. Go back and open case paper from the queue.</p>
        </div>
      </div>
    );
  }

  // Derive display-only patient info (like OPD Billing card)
  const patientInfo = {
    name: patientName ?? patient?.patientName ?? "—",
    uhid: patient?.uhid ?? patientId ?? "",
    age: patient?.age ?? patient?.ageYear ?? "",
    gender: patient?.gender ?? patient?.patientGender ?? "",
    ageGender: patient?.ageGender ?? (patient?.gender && patient?.age ? `${patient.gender} | ${patient.age}` : patient?.ageGender ?? ""),
    opdNo: patient?.opdNo ?? "",
    patientType: patient?.patientType ?? patient?.visitType ?? "—",
    doctorName: patient?.doctorName ?? patient?.doctor ?? "—",
    billingStatus: patient?.billingStatus ?? "—",
    tariffName: patient?.tariffName ?? "—",
    companyName: patient?.companyName ?? "—",
  };

  const isFemalePatient =
    typeof patientInfo.gender === "string" &&
    patientInfo.gender.trim().toLowerCase().startsWith("f");

  return (
    <FormProvider {...methods}>
      <style>{`
        @keyframes attachBtnIn {
          from { opacity: 0; transform: translateX(-14px); }
          to { opacity: 1; transform: translateX(0); }
        }
      @keyframes conditionsIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      `}</style>
      <div className="p-2 sm:p-3 pb-20">
        <div className="bg-white rounded-lg shadow-sm border">
          <div ref={attachedMenuRef}>
            {/* Sticky page title + 3-dot menu – same pattern as Admission / OPD Billing */}
            <div className="sticky top-0 z-10 bg-white rounded-t-lg px-2 py-1 sm:px-3 sm:py-1.5 border-b border-gray-200 flex items-center justify-between gap-2 shadow-sm">
              <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-1 sm:gap-2">
                <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">Case Paper - Generic</span>
              </h2>
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 justify-end">
                {/* Ref. By Doc. (moved before Templates) */}
                <div className="min-w-0 w-[10rem] sm:w-[12rem]">
                  <SearchDropdown
                    name="refByDoc"
                    control={control}
                    dataArray={[]}
                    label="Ref. By Doc."
                    placeholder="Select..."
                    isSearchable
                    isClearable
                    isFloatingLabel
                    fontSize="text-xs"
                    selectSize="h-8"
                  />
                </div>
                {/* Templates – floating label, before conditions icon */}
                <div className="min-w-0 w-[12rem] sm:w-[14rem]">
                  <SearchDropdown
                    name="emrTemplateSearch"
                    control={control}
                    label="Templates"
                    placeholder="Select template..."
                    dataArray={emrTemplateOptions}
                    isSearchable
                    isClearable
                    isFloatingLabel
                    fontSize="text-xs"
                    selectSize="h-8"
                    onChange={handleTemplateSelect}
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Conditions popup trigger */}
                <div className="relative" ref={conditionsMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowConditionsPopover((p) => !p)}
                    className="flex items-center justify-center text-[#217E90] hover:text-[#1a6575] transition-colors flex-shrink-0"
                    title="Conditions"
                    aria-label="Conditions"
                  >
                    <ClipboardDocumentListIcon className="h-5 w-5" />
                  </button>
                  {showConditionsPopover &&
                    createPortal(
                      <>
                        {/* Dim full page – portaled to avoid z-index stacking issues with Vitals labels */}
                        <div
                          className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[1px]"
                          onClick={() => setShowConditionsPopover(false)}
                          aria-hidden="true"
                        />
                        <div
                          ref={conditionsModalRef}
                          className="fixed w-[520px] max-w-[90vw] rounded-lg border border-gray-200 shadow-xl bg-[#F8FEFF] z-[9999]"
                          style={{
                            top: conditionsModalPosition.top,
                            right: conditionsModalPosition.right,
                            animation: "conditionsIn 0.18s ease-out",
                          }}
                        >
                          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 bg-[#F0F9FF]">
                            <span className="text-xs font-semibold text-[#217E90]">Conditions</span>
                            <button
                              type="button"
                              onClick={() => setShowConditionsPopover(false)}
                              className="flex h-6 w-6 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"
                              aria-label="Close conditions"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="p-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div className="flex items-center gap-2">
                                <CommonCheckbox name="cadIhd" control={control} label="CAD/IHD" fontSize="text-xs" />
                                <div className="flex-1 min-w-[8rem]">
                                  <InputField
                                    name="cadIhdNote"
                                    control={control}
                                    label=""
                                    placeholder=""
                                    fontSize="text-xs"
                                    disabled={!watch("cadIhd")}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <CommonCheckbox name="thyroid" control={control} label="Thyroid" fontSize="text-xs" />
                                <div className="flex-1 min-w-[8rem]">
                                  <InputField
                                    name="thyroidNote"
                                    control={control}
                                    label=""
                                    placeholder=""
                                    fontSize="text-xs"
                                    disabled={!watch("thyroid")}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <CommonCheckbox name="diabetes" control={control} label="Diabetes" fontSize="text-xs" />
                                <div className="flex-1 min-w-[8rem]">
                                  <InputField
                                    name="diabetesNote"
                                    control={control}
                                    label=""
                                    placeholder=""
                                    fontSize="text-xs"
                                    disabled={!watch("diabetes")}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <CommonCheckbox name="htn" control={control} label="HTN" fontSize="text-xs" />
                                <div className="flex-1 min-w-[8rem]">
                                  <InputField
                                    name="htnNote"
                                    control={control}
                                    label=""
                                    placeholder=""
                                    fontSize="text-xs"
                                    disabled={!watch("htn")}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <CommonCheckbox name="asthma" control={control} label="Asthama" fontSize="text-xs" />
                                <CommonCheckbox name="tb" control={control} label="TB" fontSize="text-xs" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </>,
                      document.body
                    )}
                </div>
                {/* Follow-up history – same circle and icon size as 3-dot */}
                <button
                  type="button"
                  onClick={() => setShowPrevFollowUpsModal(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] transition-colors flex-shrink-0"
                  title="Previous follow-up history"
                  aria-label="Previous follow-up history"
                >
                  <ClockIcon className="h-5 w-5" />
                </button>
                {/* 3-dot + popover at end of row */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAttachedButtons((p) => !p)}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] transition-colors flex-shrink-0"
                    title="More actions"
                    aria-label="More actions"
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </button>
                  {/* Attached buttons – open immediately below 3-dot, end of row */}
                  {showAttachedButtons && (
                    <div
                      className="absolute right-0 top-full z-[100] mt-1 rounded-lg border border-gray-200 bg-gray-50/95 px-2 py-2 shadow-xl"
                      style={{
                        animation: "attachBtnIn 0.25s ease-out",
                      }}
                    >
                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        {attachedButtons.map((btn, index) => {
                          const isObstDisabled = btn.id === "obstHistory" && !isFemalePatient;
                          const baseClasses =
                            "h-8 w-[8rem] flex items-center justify-center truncate px-2 text-xs font-medium rounded-md border shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1";
                          const enabledHover = " hover:scale-105";
                          const disabledStyles =
                            " bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed opacity-60";

                          return (
                            <button
                              key={btn.id}
                              type="button"
                              disabled={isObstDisabled}
                              onClick={() => {
                                if (isObstDisabled) return;
                                setShowAttachedButtons(false);
                                if (btn.id === "certificates") setShowCertificateModal(true);
                                if (btn.id === "lastIpd") handleLastIpdDsPrint();
                                if (btn.id === "opdBill") handleOpdBillClick();
                                if (btn.id === "testCompare") setShowTestCompareModal(true);
                                if (btn.id === "refer") setShowReferPatientModal(true);
                                if (btn.id === "proceedAdm") setShowProceedAdmModal(true);
                                if (btn.id === "addFiles") setShowAddFilesModal(true);
                                // TODO: wire other btn.id handlers as needed
                              }}
                              className={`${baseClasses}${
                                isObstDisabled ? disabledStyles : enabledHover
                              } ${btn.className}`}
                              title={btn.label}
                              style={{
                                animation: "attachBtnIn 0.35s ease-out both",
                                animationDelay: `${index * 40}ms`,
                              }}
                            >
                              {btn.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-1 sm:p-2">
            <div className="space-y-2">
              {/* Patient Info & Vitals – equal height, inline */}
              <div className="px-1 pt-0 pb-1">
                <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 items-stretch">
                  {/* Patient info card – same style as OPD Billing */}
                  <div className="w-full min-h-0 flex flex-col rounded-lg border border-slate-300 bg-[#F0FAFF] p-4 shadow-sm">
                    <div className="flex items-center gap-4">
                      {/* Avatar / initials */}
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-300 text-lg font-semibold text-gray-600">
                        {patientInfo.name !== "—"
                          ? String(patientInfo.name).charAt(0).toUpperCase()
                          : "?"}
                      </div>

                      {/* Patient info in 3 lines: 2 items per line */}
                      <div className="flex-1 min-w-0 text-sm leading-tight">
                        <div className="grid grid-cols-2 grid-rows-3 gap-x-10 gap-y-2" style={{ minHeight: 84 }}>
                          {/* Row 1 */}
                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              Patient Name:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patientInfo.name}
                            </span>
                          </div>

                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              UHID:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patientInfo.uhid}
                            </span>
                          </div>

                          {/* Row 2 */}
                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              OPD No:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patientInfo.opdNo}
                            </span>
                          </div>

                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              Age / Gen.:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patient?.ageGender ?? patient?.age ?? ""}
                            </span>
                          </div>

                          {/* Row 3 */}
                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              Doctor:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patientInfo.doctorName}
                            </span>
                          </div>

                          <div className="flex items-center min-w-0">
                            <span className="w-[7.5rem] text-sm font-medium text-gray-500 shrink-0 whitespace-nowrap">
                              Bld Grp.:
                            </span>
                            <span className="text-sm font-semibold text-[#217E90] flex-1 truncate whitespace-nowrap">
                              {patient?.bloodGroup ?? ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vital Signs – "Vitals" label on border line (no extra height) */}
                  <div className="rounded-lg border border-slate-300 bg-[#ECFEFF] shadow-sm flex flex-col min-h-0 h-full relative" style={{ isolation: "isolate" }}>
                    <span className="absolute left-2 -top-[0.5rem] px-1.5 text-xs font-semibold text-[#217E90] bg-[#ECFEFF] z-0">
                      Vitals
                    </span>
                    <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5 flex-1">
                      <div className="w-full min-w-0">
                        <InputField
                          name="bp"
                          control={control}
                          label="BP (mm/hg)"
                          type="bp"
                          suffix=""
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="pulse"
                          control={control}
                          label="Pulse (p/min)"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="spo2"
                          control={control}
                          label="Spo2 (%)"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="weight"
                          control={control}
                          label="Weight (Kg)"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="height"
                          control={control}
                          label="Height (cm)"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="rbs"
                          control={control}
                          label="RBS"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="temp"
                          control={control}
                          label="Temp"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="resp"
                          control={control}
                          label="Resp."
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <InputField
                          name="bmi"
                          control={control}
                          label="BMI"
                          type="text"
                          className="w-full"
                          fontSize="text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            <CasePaperGenericMainSection
              control={control}
              watch={watch}
              setValue={setValue}
              clinicalFields={clinicalFields}
              prescriptionFields={prescriptionFields}
              investServiceFields={investServiceFields}
              setShowPrescriptionModal={setShowPrescriptionModal}
              setShowServicesModal={setShowServicesModal}
              followUpInstructionOptions={followUpInstructionOptions}
            />

          </div>
        </div>
      </div>
      </div>

      <CasePaperPrescriptionModal
        isOpen={showPrescriptionModal}
        onClose={() => setShowPrescriptionModal(false)}
        control={control}
        watch={watch}
        setFocus={setFocus}
        prescriptionFields={prescriptionFields}
        removePrescriptionRow={removePrescriptionRow}
        showPrescriptionHistory={showPrescriptionHistory}
        setShowPrescriptionHistory={setShowPrescriptionHistory}
        mockPreviousPrescriptions={mockPreviousPrescriptions}
        setSelectedPrescriptionForView={setSelectedPrescriptionForView}
        handlePatchPrescription={handlePatchPrescription}
        isDuplicateDrug={isDuplicateDrug}
        drugOptions={drugOptions}
        handleDrugSelect={handleDrugSelect}
        onDrugSearch={handleDrugSearch}
        drugsLoading={drugsLoading}
        doseOptions={doseOptions}
        onDoseSearch={handleDoseSearch}
        dosesLoading={dosesLoading}
        durInOptions={durInOptions}
        drugSearchWrapperRef={drugSearchWrapperRef}
        drugDoseWrapperRef={drugDoseWrapperRef}
        drugDurationWrapperRef={drugDurationWrapperRef}
        drugDurInWrapperRef={drugDurInWrapperRef}
        drugTotalWrapperRef={drugTotalWrapperRef}
        drugAddButtonRef={drugAddButtonRef}
        prescriptionTableScrollRef={prescriptionTableScrollRef}
        focusDrugDose={focusDrugDose}
        focusDrugDuration={focusDrugDuration}
        focusDrugDurIn={focusDrugDurIn}
        focusDrugTotal={focusDrugTotal}
        focusDrugAddButton={focusDrugAddButton}
        focusDrugSearch={focusDrugSearch}
        handleAddDrug={handleAddDrug}
      />

      <CasePaperServicesModal
        isOpen={showServicesModal}
        onClose={() => setShowServicesModal(false)}
        control={control}
        watch={watch}
        investServiceFields={investServiceFields}
        removeInvestService={removeInvestService}
        handleInvestTemplateToggle={handleInvestTemplateToggle}
        handleAddInvestService={handleAddInvestService}
        investigationTemplateOptions={investigationTemplateOptions}
        investigationServiceOptions={investigationServiceOptions}
        onServiceSearch={handleServiceSearch}
        servicesLoading={servicesLoading}
        investigationTableScrollRef={investigationTableScrollRef}
      />

      {/* Prescription medicines view modal (opened from Eye in Previous Prescriptions) */}
      <CommonModal
        isOpen={!!selectedPrescriptionForView}
        onClose={() => setSelectedPrescriptionForView(null)}
        title={selectedPrescriptionForView ? `Prescription - ${selectedPrescriptionForView.date} (${selectedPrescriptionForView.doctorName})` : "Prescription Medicines"}
        size="lg"
        disableBackdropClose={false}
        DialogContent={
          selectedPrescriptionForView ? (
            <div className="max-h-[60vh] overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-center font-medium text-gray-600 w-20">Action</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Drug Name</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Generic Name</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Dose</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Duration</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Dur.In</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Total Qty</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Instruction</th>
                    <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPrescriptionForView.medicines?.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-2 py-4 text-center text-gray-400">No medicines in this prescription</td>
                    </tr>
                  ) : (
                    selectedPrescriptionForView.medicines.map((med, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="px-2 py-1">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => handlePatchSingleMedicine(med)}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] bg-white"
                              title="Add to current prescription"
                            >
                              <ArrowDownCircleIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-1.5">{med.drugName ?? ""}</td>
                        <td className="px-2 py-1.5">{med.genericName ?? ""}</td>
                        <td className="px-2 py-1.5">{med.dose ?? ""}</td>
                        <td className="px-2 py-1.5">{med.duration ?? ""}</td>
                        <td className="px-2 py-1.5">{med.durIn ?? ""}</td>
                        <td className="px-2 py-1.5">{med.totalQty ?? ""}</td>
                        <td className="px-2 py-1.5">{med.instruction ?? ""}</td>
                        <td className="px-2 py-1.5">{med.remark ?? ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null
        }
      />

      {/* View Case Paper Modal (opened from Eye in Previous Follow-ups) */}
      <CommonModal
        isOpen={!!selectedCasePaperForView}
        onClose={() => setSelectedCasePaperForView(null)}
        title={selectedCasePaperForView ? `Case Paper - ${selectedCasePaperForView.date} (${selectedCasePaperForView.doctorName})` : "View Case Paper"}
        size="lg"
        disableBackdropClose={false}
        DialogContent={
          selectedCasePaperForView?.casePaper ? (
            <div className="max-h-[70vh] overflow-auto space-y-4 p-2 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "BP", value: selectedCasePaperForView.casePaper.bp },
                  { label: "Pulse", value: selectedCasePaperForView.casePaper.pulse },
                  { label: "SpO2", value: selectedCasePaperForView.casePaper.spo2 },
                  { label: "Weight", value: selectedCasePaperForView.casePaper.weight },
                  { label: "Height", value: selectedCasePaperForView.casePaper.height },
                  { label: "RBS", value: selectedCasePaperForView.casePaper.rbs },
                  { label: "Temp", value: selectedCasePaperForView.casePaper.temp },
                  { label: "Resp", value: selectedCasePaperForView.casePaper.resp },
                  { label: "BMI", value: selectedCasePaperForView.casePaper.bmi },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
                    <span className="text-gray-500">{label}:</span> <span className="font-medium">{value || "—"}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-medium text-gray-600 mb-1">Chief Complaints</p>
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 whitespace-pre-wrap">{selectedCasePaperForView.casePaper.chiefComplaints || "—"}</div>
              </div>
              <div>
                <p className="font-medium text-gray-600 mb-1">History</p>
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 whitespace-pre-wrap">{selectedCasePaperForView.casePaper.history || "—"}</div>
              </div>
              <div>
                <p className="font-medium text-gray-600 mb-1">Findings / Diagnosis</p>
                <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">Findings: {selectedCasePaperForView.casePaper.findings || "—"} | Diagnosis: {selectedCasePaperForView.casePaper.diagnosis || "—"}</div>
              </div>
              <div>
                <p className="font-medium text-gray-600 mb-1">Prescriptions</p>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Drug Name</th>
                        <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Dose</th>
                        <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Duration</th>
                        <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Instruction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedCasePaperForView.casePaper.prescriptions ?? []).length === 0 ? (
                        <tr><td colSpan={4} className="px-2 py-2 text-gray-400">No medicines</td></tr>
                      ) : (
                        selectedCasePaperForView.casePaper.prescriptions.map((m, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="px-2 py-1">{m.drugName ?? ""}</td>
                            <td className="px-2 py-1">{m.dose ?? ""}</td>
                            <td className="px-2 py-1">{m.duration ?? ""} {m.durIn ?? ""}</td>
                            <td className="px-2 py-1">{m.instruction ?? ""}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {(selectedCasePaperForView.casePaper.investServices ?? []).length > 0 && (
                <div>
                  <p className="font-medium text-gray-600 mb-1">Investigations</p>
                  <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                    {selectedCasePaperForView.casePaper.investServices.map((s, i) => (
                      <span key={i} className="mr-2">{s.serviceName}</span>
                    ))}
                  </div>
                </div>
              )}
              {selectedCasePaperForView.casePaper.myThought && (
                <div>
                  <p className="font-medium text-gray-600 mb-1">My Thought</p>
                  <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">{selectedCasePaperForView.casePaper.myThought}</div>
                </div>
              )}
            </div>
          ) : null
        }
      />

      {/* Previous Follow-ups Modal (uses common modal + icons) */}
      <CommonModal
        isOpen={showPrevFollowUpsModal}
        onClose={() => setShowPrevFollowUpsModal(false)}
        title="Previous Follow-up Dates"
        size="lg"
        disableBackdropClose={false}
        DialogContent={
          <div className="max-h-[60vh] overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-2 py-1.5 text-center font-medium text-gray-600">
                    Action
                  </th>
                  <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">
                    Date
                  </th>
                  <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">
                    Doctor Name
                  </th>
                  <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600">
                    FollowUp Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {prevFollowUps.map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50/50 ${i < 2 ? "bg-amber-50/50" : ""}`}>
                    <td className="px-2 py-1">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleViewCasePaper(row)}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] bg-white"
                          title="View case paper"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePatchCasePaper(row)}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] bg-white"
                          title="Patch case paper to current form"
                        >
                          <ArrowDownCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1">
                      {row.date}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1">
                      {row.doctorName}
                    </td>
                    <td className="border-b border-gray-100 px-2 py-1">
                      {row.followUpDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      />

      {/* Certificate form – opened from 3-dot Certificates button */}
      {/* OPD Bill Modal – opens OPD Billing screen in modal, same module */}
      <TestCompareModal
        isOpen={showTestCompareModal}
        onClose={() => setShowTestCompareModal(false)}
        patientInfo={patientInfo}
      />

      <ReferPatientModal
        isOpen={showReferPatientModal}
        onClose={() => setShowReferPatientModal(false)}
        patientInfo={patientInfo}
      />

      <ProceedToAdmissionModal
        isOpen={showProceedAdmModal}
        onClose={() => setShowProceedAdmModal(false)}
        patientInfo={patientInfo}
      />

      <AddFilesDocsModal
        isOpen={showAddFilesModal}
        onClose={() => setShowAddFilesModal(false)}
        patientInfo={patientInfo}
      />

      <CommonModal
        isOpen={showOpdBillModal}
        onClose={() => setShowOpdBillModal(false)}
        title="OPD Billing"
        size="xl"
        disableBackdropClose={false}
        DialogContent={
          <div className="min-h-[60vh] -mt-3 sm:-mt-4">
            <OPDBilling
              embeddedInModal
              initialState={{
                patientName: patientName ?? patient?.patientName ?? "",
                visitId: patient?.visitId ?? state?.visitId,
                patientId: patientId ?? patient?.regId ?? patient?.patientId,
                uhid: patient?.uhid ?? state?.uhid,
              }}
            />
          </div>
        }
      />

      <CertificateFormModal
        isOpen={showCertificateModal}
        onClose={() => setShowCertificateModal(false)}
        patientInfo={patientInfo}
      />

      {/* Fixed bottom right – same pattern as Bill / Registration / Admission */}
      <div className="fixed bottom-4 right-4 z-50 flex gap-2 sm:gap-3 pr-4 sm:pr-6">
        <CommonButton
          label="Reset"
          onClick={() => methods.reset()}
          variant="danger-outline"
          size="sm"
          type="button"
          className="flex-none px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label={saving ? "Saving..." : "Save"}
          variant="primary-filled"
          size="sm"
          type="button"
          disabled={saving}
          onClick={handleSaveCasePaper}
          className="flex-none px-3 md:px-5 text-xs md:text-sm bg-[#217E90] hover:bg-[#1a6575]"
        />
      </div>

      {/* Hidden content for Last IPD DS print – empty placeholder, will show discharge summary in future */}
      <div
        ref={lastIpdDsPrintRef}
        className="bg-white p-6 text-sm"
        style={{ position: "fixed", left: "-9999px", top: 0, width: "210mm" }}
      >
        <div className="text-center border-b border-gray-300 pb-2 mb-4">
          <h2 className="text-lg font-bold text-[#217E90]">Discharge Summary</h2>
          <p className="text-xs text-gray-600">Last IPD Discharge Summary</p>
        </div>
        <div className="space-y-2 text-gray-600">
          <p className="text-center py-8">— No discharge summary data —</p>
          <p className="text-xs text-center text-gray-400">(Content will be populated when API is integrated)</p>
        </div>
      </div>
    </FormProvider>
  );
}

export default CasePaper;
